import { useState } from 'react';
import { Badge, Button, Destructive, Icon, Input, Reason, useToast } from '@efira/ui';

import {
  useActivateChannel,
  useAttachChannel,
  useChannels,
  useDetachChannel,
} from '../../api/queries';
import { accounts as accountsWord } from '../../lib/plural';

/**
 * Аккаунты TikTok: строка аккаунта, удаление с подтверждением, добавление,
 * аккаунт по умолчанию.
 *
 * РОЛИ РАЗДЕЛЕНЫ (решение владельца 2026-09-29, п. 3; находка 22 аудита).
 * Здесь УПРАВЛЯЮТ СПИСКОМ: добавить, удалить, назначить по умолчанию.
 * Левая колонка консоли только ВЫБИРАЕТ, с какого аккаунта пойдёт эфир, -
 * и добавить аккаунт там нельзя, её пункт ведёт сюда. Список один и тот же:
 * две правды о привязанных каналах разошлись бы в первый же день.
 */
export function TikTokAccounts() {
  const channels = useChannels();
  const add = useAttachChannel();
  const remove = useDetachChannel();
  const activate = useActivateChannel();
  const toast = useToast();
  const [handle, setHandle] = useState('');

  const rows = channels.data?.channels ?? [];
  const limit = channels.data?.limit;

  return (
    <div data-tiktok="root" className="flex max-w-xl flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h2 className="text-foreground text-xl font-semibold">Аккаунты TikTok</h2>
        <p className="text-foreground/80 text-sm">Откуда берутся события эфира.</p>
      </header>

      <p className="text-foreground/90 text-sm" data-tiktok="role">
        Здесь список: добавить, удалить, назначить аккаунт по умолчанию — с него пойдёт эфир.
        Переключить аккаунт перед эфиром можно и в консоли, в разделе «Подключение». Пароль от
        TikTok мы не спрашиваем и получить его не можем.
      </p>

      {rows.length === 0 ? (
        <p data-tiktok="empty" className="text-foreground/90 text-sm">
          Аккаунтов пока нет. Добавьте логин — тот, что стоит в адресе вашего профиля после «@».
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((channel) => (
            <li
              key={channel.id}
              data-tiktok-row={channel.id}
              data-control-row="tiktok-row"
              className="border-foreground/10 rounded-lg @container flex items-center gap-3 border p-3"
            >
              {/* Аватар приходит вместе с каналом, когда он известен
                  вендору; до первого эфира его нет, и вместо него - значок,
                  а не пустое место. */}
              <span className="bg-muted grid size-10 shrink-0 place-items-center rounded-full">
                <Icon name="person" size="sm" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    data-proper
                    className="text-foreground min-w-0 truncate text-sm font-semibold"
                  >
                    @{channel.handle}
                  </span>

                  {/* Метка - РЯДОМ С ЛОГИНОМ, а не у правого края: там она
                      сливалась с кнопкой удаления в один блок. */}
                  {channels.data?.activeChannelId === channel.id && (
                    <Badge data-tiktok-default={channel.id} variant="default">
                      по умолчанию
                    </Badge>
                  )}
                </span>
                <span className="text-foreground/90 block truncate text-xs">
                  {channel.lastLiveAt === null || channel.lastLiveAt === undefined
                    ? 'Эфиров ещё не было'
                    : `Последний эфир: ${new Date(channel.lastLiveAt).toLocaleDateString('ru-RU')}`}
                </span>
              </span>

              {channels.data?.activeChannelId !== channel.id && (
                <Button
                  variant="ghost"
                  size="s"
                  className="shrink-0"
                  data-tiktok-make-default={channel.id}
                  loading={activate.isPending}
                  onClick={() => {
                    activate.mutate(channel.id, {
                      onSuccess: () => {
                        toast.show({
                          tone: 'success',
                          title: `@${channel.handle} — по умолчанию`,
                          text: 'Эфир пойдёт с этого аккаунта.',
                        });
                      },
                    });
                  }}
                >
                  <span className="@max-[420px]:hidden">Сделать по умолчанию</span>
                  <span className="@[420px]:hidden">По умолчанию</span>
                </Button>
              )}

              {/* Корзина, а не крест: крест читается как «закрыть», и
                  площадь нажатия у него была в один значок. */}
              <Destructive
                id="tiktok-delete"
                className="shrink-0"
                data={{ 'data-tiktok-remove': channel.id }}
                ariaLabel={`Удалить @${channel.handle}`}
                icon={<Icon name="delete" size="sm" />}
                title="Удалить аккаунт?"
                confirm="Удалить"
                onConfirm={() => {
                  remove.mutate(channel.id, {
                    onSuccess: () => {
                      toast.show({ tone: 'success', title: `@${channel.handle} удалён` });
                    },
                  });
                }}
              >
                @{channel.handle} перестанет быть источником событий. Игры, звуки и настройки
                останутся — уйдёт только связь с этим аккаунтом TikTok.
              </Destructive>
            </li>
          ))}
        </ul>
      )}

      <form
        data-control-row="tiktok-add"
        className="@container flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate(handle.trim(), {
            onSuccess: () => {
              toast.show({ tone: 'success', title: 'Аккаунт добавлен' });
              setHandle('');
            },
            onError: (error: unknown) => {
              toast.show({
                tone: 'warning',
                title: 'Не добавился',
                text: (error as { message?: string }).message ?? 'Проверьте логин.',
              });
            },
          });
        }}
      >
        {/* Подпись живёт ПОДСКАЗКОЙ В ПОЛЕ, а не строкой над ним: отдельная
            строка «Логин аккаунта» разрывала ритм между карточкой и формой,
            ничего не добавляя к «@username». Имя для чтеца остаётся.

            Знак «@» - именно в подсказке, а не отдельным значком поверх
            поля: наложенный элемент пересекается с полем, и ворота строк
            управления читают это как налезающие друг на друга органы - они
            правы, отличить одно от другого по разметке нельзя. */}
        <Input
          data-tiktok="handle"
          aria-label="Логин аккаунта TikTok"
          placeholder="@ваш_логин"
          value={handle}
          onChange={(event) => {
            setHandle(event.target.value);
          }}
          className="min-w-0 flex-1"
        />

        {/* Кнопка неактивна при пустом поле: нажатие «добавить ничего» -
            это отказ, который можно было не показывать. */}
        <Button
          variant="primary"
          className="shrink-0"
          type="submit"
          data-tiktok="add"
          loading={add.isPending}
          disabled={handle.trim() === ''}
          aria-describedby={handle.trim() === '' ? 'tiktok-add-why' : undefined}
        >
          Добавить
        </Button>
      </form>

      {handle.trim() === '' && (
        <Reason id="tiktok-add-why">Введите логин — кнопка станет доступна.</Reason>
      )}

      <p className="text-foreground/90 text-sm">
        {accountsWord(rows.length)}
        {limit === null || limit === undefined ? ' — без ограничения' : ` из ${String(limit)}`}.
      </p>
    </div>
  );
}
